import { useState, useEffect, useCallback, useRef } from 'react'
import axios from 'axios'

const API = process.env.REACT_APP_API_URL
const TIEMPO_POR_PREGUNTA = 7 // segundos

const NOMBRE_FORMA = { pasado: 'Pasado simple', participio: 'Participio pasado' }
const COLOR_CATEGORIA = { regular: '#3B82F6', irregular: '#EF4444' }
const NOMBRE_CATEGORIA = { regular: 'Regular', irregular: 'Irregular' }

/* Juego de Conjugacion Rapida: se muestra un verbo en infinitivo
   y se pide su pasado simple o participio, contra un cronometro
   corto, con 4 opciones para elegir. Se puede jugar tantas veces
   como se quiera. */
export default function ConjugacionJuego({ usuario, onCambio }) {
  const [cargando,   setCargando]   = useState(true)
  const [preguntas,  setPreguntas]  = useState([])
  const [indice,     setIndice]     = useState(0)
  const [elegida,    setElegida]    = useState(null)
  const [validado,   setValidado]   = useState(false)
  const [aciertos,   setAciertos]   = useState(0)
  const [terminado,  setTerminado]  = useState(false)
  const [enviando,   setEnviando]   = useState(false)
  const [resultado,  setResultado]  = useState(null)
  const [error,      setError]      = useState(null)
  const [tiempo,     setTiempo]     = useState(TIEMPO_POR_PREGUNTA)

  const avanceRef = useRef(null)

  const nuevaPartida = useCallback(() => {
    setCargando(true)
    setError(null)
    setIndice(0)
    setElegida(null)
    setValidado(false)
    setAciertos(0)
    setTerminado(false)
    setResultado(null)
    setTiempo(TIEMPO_POR_PREGUNTA)
    axios.get(`${API}/juegos/conjugacion?cantidad=10`).then(res => {
      setPreguntas(res.data.datos || [])
    }).catch(err => {
      console.error('Error cargando conjugación:', err)
      setError('No pudimos cargar las preguntas. Intenta de nuevo.')
    }).finally(() => setCargando(false))
  }, [])

  useEffect(() => { nuevaPartida() }, [nuevaPartida])

  function elegirOpcion(i) {
    if (validado || terminado || !preguntas[indice]) return
    setElegida(i)
    setValidado(true)
    if (i === preguntas[indice].correcta) setAciertos(a => a + 1)
    if (avanceRef.current) clearTimeout(avanceRef.current)
    avanceRef.current = setTimeout(() => {
      if (indice < preguntas.length - 1) {
        setIndice(idx => idx + 1)
        setElegida(null)
        setValidado(false)
        setTiempo(TIEMPO_POR_PREGUNTA)
      } else {
        setTerminado(true)
      }
    }, 1300)
  }

  // Cronometro por pregunta: si se agota el tiempo sin responder, cuenta como fallo.
  useEffect(() => {
    if (cargando || terminado || validado || !preguntas.length) return
    if (tiempo <= 0) {
      elegirOpcion(-1) // -1 nunca coincide con un indice real, cuenta como fallo
      return
    }
    const t = setTimeout(() => setTiempo(s => s - 1), 1000)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tiempo, cargando, terminado, validado, preguntas.length])

  useEffect(() => () => { if (avanceRef.current) clearTimeout(avanceRef.current) }, [])

  async function reclamarXp() {
    if (enviando || !usuario?.id) return
    setEnviando(true)
    setError(null)
    try {
      const res = await axios.post(`${API}/juegos/conjugacion/completar`, {
        usuario_id: usuario.id, aciertos, total_preguntas: preguntas.length
      })
      setResultado(res.data)
      if (onCambio) onCambio()
    } catch (err) {
      console.error('Error guardando resultado de conjugación:', err)
      setError('No pudimos guardar tu resultado. Intenta de nuevo.')
    } finally {
      setEnviando(false)
    }
  }

  if (cargando) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--gris-texto)' }}>
        Cargando verbos...
      </div>
    )
  }

  if (!preguntas.length) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--gris-texto)' }}>
        <div style={{ fontSize: 44, marginBottom: 12 }}>⚡</div>
        <p>Todavía no hay verbos cargados. ¡Vuelve pronto!</p>
      </div>
    )
  }

  return (
    <div style={{ animation: 'fadeIn .3s ease both', maxWidth: 640 }}>
      <div style={{ marginBottom: 22 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800 }}>⚡ Conjugación Rápida</h1>
        <p style={{ color: 'var(--gris-texto)', fontSize: 14, marginTop: 4 }}>
          Conjuga verbos en inglés contra el reloj.
        </p>
      </div>

      {error && (
        <div style={{ background: '#FEE2E2', color: '#B91C1C', borderRadius: 10, padding: '10px 14px', fontSize: 13, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {!terminado && (() => {
        const p = preguntas[indice]
        const colorCat = COLOR_CATEGORIA[p.categoria] || 'var(--azul)'
        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--azul)' }}>Verbo {indice + 1} de {preguntas.length}</span>
              <div style={{ flex: 1, height: 6, background: 'var(--gris-borde)', borderRadius: 999, overflow: 'hidden' }}>
                <div style={{ width: `${(indice / preguntas.length) * 100}%`, height: '100%', background: 'var(--dorado)', borderRadius: 999, transition: 'width .3s ease' }} />
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#F59E0B' }}>✓ {aciertos}</span>
            </div>

            <div style={{ background: '#fff', border: '1px solid var(--gris-borde)', borderRadius: 18, padding: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ background: colorCat, color: '#fff', fontSize: 11, fontWeight: 800, padding: '3px 10px', borderRadius: 999 }}>
                    {NOMBRE_CATEGORIA[p.categoria] || p.categoria}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--gris-texto)' }}>{p.nivel}</span>
                </div>
                {!validado && (
                  <span style={{ fontSize: 13, fontWeight: 800, color: tiempo <= 3 ? '#EF4444' : 'var(--azul)' }}>
                    ⏱ {tiempo}s
                  </span>
                )}
              </div>

              <div style={{ height: 4, background: 'var(--gris-borde)', borderRadius: 999, overflow: 'hidden', marginBottom: 20 }}>
                <div style={{
                  width: `${(tiempo / TIEMPO_POR_PREGUNTA) * 100}%`, height: '100%',
                  background: tiempo <= 3 ? '#EF4444' : '#22C55E', borderRadius: 999, transition: 'width 1s linear'
                }} />
              </div>

              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <p style={{ fontSize: 12.5, color: 'var(--gris-texto)', marginBottom: 6 }}>
                  {NOMBRE_FORMA[p.forma]} de:
                </p>
                <h2 style={{ fontSize: 30, fontWeight: 800 }}>{p.verbo_base}</h2>
                {p.traduccion && (
                  <p style={{ fontSize: 13, color: 'var(--gris-texto)', marginTop: 4 }}>({p.traduccion})</p>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {p.opciones.map((op, i) => {
                  const esCorrecta = i === p.correcta
                  const esElegida = elegida === i
                  let fondo = '#fff', borde = 'var(--gris-borde)'
                  if (validado && esCorrecta) { fondo = '#DCFCE7'; borde = '#22C55E' }
                  else if (validado && esElegida && !esCorrecta) { fondo = '#FEE2E2'; borde = '#EF4444' }
                  return (
                    <button key={i} onClick={() => elegirOpcion(i)} disabled={validado}
                      style={{ textAlign: 'center', padding: '14px', borderRadius: 10, border: '1.5px solid ' + borde,
                        background: fondo, color: '#1A1A2E', fontFamily: 'inherit', fontSize: 15, fontWeight: 700,
                        cursor: validado ? 'default' : 'pointer' }}>
                      {op}
                    </button>
                  )
                })}
              </div>

              {validado && elegida === -1 && (
                <div style={{ background: '#FEF3C7', borderRadius: 10, padding: 12, marginTop: 16 }}>
                  <p style={{ fontSize: 12.5, color: 'var(--gris-texto)' }}>⏱ Se acabó el tiempo.</p>
                </div>
              )}
            </div>
          </div>
        )
      })()}

      {terminado && (
        <div style={{ background: '#fff', border: '1px solid var(--gris-borde)', borderRadius: 18, padding: 26, textAlign: 'center' }}>
          <div style={{ fontSize: 44, marginBottom: 10 }}>{aciertos >= preguntas.length * 0.7 ? '🎉' : '💪'}</div>
          <h2 style={{ fontSize: 19, fontWeight: 800, marginBottom: 6 }}>¡Partida terminada!</h2>
          <p style={{ fontSize: 14, color: 'var(--gris-texto)', marginBottom: 20 }}>
            Acertaste {aciertos} de {preguntas.length} verbos.
          </p>

          {resultado ? (
            <div>
              <div style={{ background: 'linear-gradient(135deg,#065F46,#059669)', color: '#fff', borderRadius: 14, padding: 18, marginBottom: 18 }}>
                <div style={{ fontSize: 20, fontWeight: 800 }}>+{resultado.xp_ganado} XP ganados</div>
              </div>
              <button onClick={nuevaPartida}
                style={{ width: '100%', padding: '13px', borderRadius: 12, border: 'none', background: 'var(--azul)', color: '#fff',
                  fontFamily: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                🔁 Jugar de nuevo
              </button>
            </div>
          ) : (
            <button onClick={reclamarXp} disabled={enviando}
              style={{ width: '100%', padding: '14px', borderRadius: 12, border: 'none', fontFamily: 'inherit',
                fontSize: 15, fontWeight: 700, cursor: enviando ? 'not-allowed' : 'pointer',
                background: enviando ? '#CBD5E1' : 'var(--dorado)', color: '#fff' }}>
              {enviando ? 'Guardando...' : `Reclamar +${aciertos * 3} XP`}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
